import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Zap, Clock, Info, Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { Device, deviceService } from "@/services/deviceService";
import { cn } from "@/lib/utils";

interface BulkClearCacheDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  devices: Device[];
  onSuccess?: () => void;
}

export default function BulkClearCacheDialog({
  open,
  onOpenChange,
  devices,
  onSuccess,
}: BulkClearCacheDialogProps) {
  const [scheduleType, setScheduleType] = useState<"instant" | "scheduled">("instant");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [companyOpen, setCompanyOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // Benzersiz firmaları al
  const companies = useMemo(() => {
    const uniqueCompanies = new Map();
    devices.forEach(device => {
      if (device.company) {
        uniqueCompanies.set(device.company._id, device.company.name);
      }
    });
    return Array.from(uniqueCompanies.entries()).map(([id, name]) => ({ id, name }));
  }, [devices]);

  // Filtrelenmiş firmalar (arama için)
  const filteredCompanies = useMemo(() => {
    if (!searchQuery) return companies;
    return companies.filter(c =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [companies, searchQuery]);

  // Filtrelenmiş cihazlar
  const filteredDevices = useMemo(() => {
    const onlineDevices = devices.filter(d => d.isOnline);
    if (selectedCompany === "all") {
      return onlineDevices;
    }
    return onlineDevices.filter(d => d.company?._id === selectedCompany);
  }, [devices, selectedCompany]);

  const onlineDevices = filteredDevices;

  const handleSubmit = async () => {
    if (scheduleType === "scheduled" && (!scheduledDate || !scheduledTime)) {
      toast.error("Lütfen tarih ve saat seçin");
      return;
    }

    try {
      setLoading(true);
      let successCount = 0;

      if (scheduleType === "instant") {
        for (const device of onlineDevices) {
          try {
            await deviceService.clearCache(device._id);
            successCount++;
          } catch (error) {
            console.error(`${device.name} cache temizleme hatası:`, error);
          }
        }
        toast.success(`${successCount} cihazın cache'i temizleniyor`);
      } else {
        const scheduledDateTime = new Date(`${scheduledDate}T${scheduledTime}`);
        for (const device of onlineDevices) {
          try {
            await deviceService.clearCache(device._id, scheduledDateTime);
            successCount++;
          } catch (error) {
            console.error(`${device.name} zamanlanmış cache hatası:`, error);
          }
        }
        toast.success(
          `${successCount} cihaz için cache temizleme ${scheduledDateTime.toLocaleString("tr-TR")} tarihine zamanlandı`
        );
      }

      onOpenChange(false);
      resetForm();
      onSuccess?.();
    } catch (error) {
      toast.error("Toplu cache temizleme sırasında hata oluştu");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setScheduleType("instant");
    setScheduledDate("");
    setScheduledTime("");
    setSelectedCompany("all");
    setSearchQuery("");
  };

  const handleSelectCompany = (companyId: string) => {
    setSelectedCompany(companyId);
    setCompanyOpen(false);
    setSearchQuery("");
  };

  const isFormValid = () => {
    if (scheduleType === "instant") return true;
    return scheduledDate && scheduledTime;
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Toplu Cache Temizleme</DialogTitle>
        </DialogHeader>

        {/* Firma Seçimi */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">
            Firma
          </Label>
          <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={companyOpen}
                className="w-full justify-between"
              >
                {selectedCompany === "all"
                  ? "Tüm Firmalar"
                  : companies.find((company) => company.id === selectedCompany)?.name || "Firma seçin"}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
              <div className="flex flex-col">
                {/* Manuel Arama Input */}
                <div className="border-b px-3 py-2">
                  <Input
                    placeholder="Firma ara..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-9"
                  />
                </div>

                {/* Firma Listesi */}
                <div className="max-h-64 overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400">
                  {/* Tüm Firmalar */}
                  <div
                    className={cn(
                      "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                      selectedCompany === "all" && "bg-blue-50"
                    )}
                    onClick={() => handleSelectCompany("all")}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedCompany === "all" ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-sm">Tüm Firmalar</span>
                  </div>

                  {/* Filtrelenmiş Firmalar */}
                  {filteredCompanies.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                      Firma bulunamadı.
                    </div>
                  ) : (
                    filteredCompanies.map((company) => (
                      <div
                        key={company.id}
                        className={cn(
                          "flex items-center px-3 py-2 cursor-pointer hover:bg-gray-100 transition-colors",
                          selectedCompany === company.id && "bg-blue-50"
                        )}
                        onClick={() => handleSelectCompany(company.id)}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedCompany === company.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span className="text-sm">{company.name}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {/* Bilgilendirme Banner */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">
              {onlineDevices.length} çevrimiçi cihazın cache'i temizlenecek
            </p>
            <p>
              Müzik dosyaları hariç tüm cache temizlenecektir. İşlem tamamlandıktan sonra
              cihazlar otomatik olarak yeniden başlatılacaktır.
            </p>
          </div>
        </div>

        {/* Seçenekler */}
        <div className="space-y-3">
          {/* Anlık Temizle */}
          <div
            className={`flex items-start gap-4 border-2 rounded-lg p-4 cursor-pointer transition-all ${
              scheduleType === "instant"
                ? "border-blue-500 bg-blue-50 shadow-sm"
                : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            }`}
            onClick={() => setScheduleType("instant")}
          >
            <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1 pt-1">
              <h3 className="text-base font-semibold mb-1">Anlık Temizle</h3>
              <p className="text-sm text-muted-foreground">
                Tüm cihazların cache'i hemen temizlenecek
              </p>
            </div>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-2 ${
              scheduleType === "instant"
                ? "border-blue-500 bg-blue-500"
                : "border-gray-300"
            }`}>
              {scheduleType === "instant" && (
                <div className="w-2 h-2 rounded-full bg-white"></div>
              )}
            </div>
          </div>

          {/* Zamanla */}
          <div
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              scheduleType === "scheduled"
                ? "border-blue-500 bg-blue-50 shadow-sm"
                : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
            }`}
          >
            <div
              className="flex items-start gap-4"
              onClick={() => setScheduleType("scheduled")}
            >
              <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 pt-1">
                <h3 className="text-base font-semibold mb-1">Zamanla</h3>
                <p className="text-sm text-muted-foreground">
                  Tüm cihazların cache'i belirli bir tarih/saatte temizlenecek
                </p>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-2 ${
                scheduleType === "scheduled"
                  ? "border-blue-500 bg-blue-500"
                  : "border-gray-300"
              }`}>
                {scheduleType === "scheduled" && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
            </div>

            {/* DateTime Picker */}
            {scheduleType === "scheduled" && (
              <div className="mt-4 space-y-3 pl-16">
                <div className="space-y-2">
                  <Label htmlFor="bulk-date" className="text-sm font-medium">Tarih</Label>
                  <Input
                    id="bulk-date"
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={today}
                    className="w-full"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-time" className="text-sm font-medium">Saat</Label>
                  <Input
                    id="bulk-time"
                    type="time"
                    value={scheduledTime}
                    onChange={(e) => setScheduledTime(e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              resetForm();
            }}
            disabled={loading}
          >
            İptal
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid() || loading}
          >
            {loading ? "İşleniyor..." : "Temizle"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
